import { useParams, useLocation, useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import { Form, Button, Row, Col, Table, Card, Alert, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from "../../../../utils/ApiUtils";
import PageTitle from "../../../layouts/PageTitle";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
    ArrowLeft,
    Save,
    X,
    FileX,
    Edit3,
    Calculator,
    Calendar,
    FileText,
    AlertTriangle,
    CheckCircle,
    Plus,
    Trash2,
    Info,
    ArrowRight
} from 'lucide-react';
import swal from "sweetalert";
import { toast } from "sonner";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

// Helper function to format numbers as Swedish currency (SEK)
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};

const EditVerification = () => {
    const { companyId, verificationId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const { verificationData, from } = location.state || {};

    const [accounts, setAccounts] = useState([]);
    const [formData, setFormData] = useState({
        date: verificationData ? new Date(verificationData.date) : new Date(),
        description: verificationData ? verificationData.description : "",
        attachments: verificationData ? verificationData.attachments : [],
        transactions: verificationData ? verificationData.transactions.map(tx => ({
            ...tx,
            debit: tx.transaction_type === 'debit' ? parseFloat(tx.amount) : "",
            credit: tx.transaction_type === 'credit' ? parseFloat(tx.amount) : ""
        })) : [],
    });

    const [annulmentTransactions, setAnnulmentTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingVerification, setSavingVerification] = useState(false);
    const [error, setError] = useState(null);
    const [balanceStatus, setBalanceStatus] = useState({ message: "", variant: "secondary" });

    // Function to check if transactions are balanced
    const areTransactionsBalanced = () => {
        if (!formData.transactions || formData.transactions.length === 0) {
            return false;
        }

        let totalDebit = 0;
        let totalCredit = 0;

        formData.transactions.forEach((transaction) => {
            const debitAmount = parseFloat(transaction.debit || 0);
            const creditAmount = parseFloat(transaction.credit || 0);

            totalDebit += debitAmount;
            totalCredit += creditAmount;
        });

        // Allow for small rounding differences (within 1 öre)
        const difference = Math.abs(totalDebit - totalCredit);
        return difference < 0.01;
    };

    // Enhanced form validation function that includes balance check
    const isFormValid = () => {
        // Check if description is provided
        if (!formData.description || formData.description.trim() === '') {
            return false;
        }

        // Check if there are transactions
        if (!formData.transactions || formData.transactions.length === 0) {
            return false;
        }

        // Check if any transaction is missing required data
        const hasInvalidTransaction = formData.transactions.some(transaction => {
            const hasAccount = transaction.account_id && transaction.account_number.trim() !== '';
            const hasAmount = (transaction.debit && parseFloat(transaction.debit) > 0) ||
                (transaction.credit && parseFloat(transaction.credit) > 0);
            return !hasAccount || !hasAmount;
        });

        if (hasInvalidTransaction) {
            return false;
        }

        // Check if transactions are balanced
        return areTransactionsBalanced();
    };

    // Function to get balance status with detailed information
    const getBalanceStatus = () => {
        if (!formData.transactions || formData.transactions.length === 0) {
            return {
                isBalanced: false,
                message: "Inga transaktioner",
                variant: "secondary",
                difference: 0
            };
        }

        let totalDebit = 0;
        let totalCredit = 0;

        formData.transactions.forEach((transaction) => {
            totalDebit += parseFloat(transaction.debit || 0);
            totalCredit += parseFloat(transaction.credit || 0);
        });

        const difference = totalDebit - totalCredit;
        const isBalanced = Math.abs(difference) < 0.01;

        if (isBalanced) {
            return {
                isBalanced: true,
                message: "Debet och Kredit balanserar",
                variant: "success",
                difference: 0,
                totalDebit,
                totalCredit
            };
        } else if (difference > 0) {
            return {
                isBalanced: false,
                message: `Debet är ${formatCurrency(difference)} mer än kredit`,
                variant: "danger",
                difference,
                totalDebit,
                totalCredit
            };
        } else {
            return {
                isBalanced: false,
                message: `Kredit är ${formatCurrency(Math.abs(difference))} mer än debet`,
                variant: "danger",
                difference,
                totalDebit,
                totalCredit
            };
        }
    };

    useEffect(() => {
        if (!verificationData) {
            fetchVerificationData();
        } else {
            generateAnnulmentTransactions();
        }
        fetchAccounts();
    }, [verificationId]);

    useEffect(() => {
        calculateBalance();
    }, [formData.transactions]);

    const calculateBalance = () => {
        let totalDebit = 0;
        let totalCredit = 0;

        formData.transactions.forEach((row) => {
            totalDebit += parseFloat(row.debit || 0);
            totalCredit += parseFloat(row.credit || 0);
        });

        const diff = Math.abs(totalDebit - totalCredit);

        if (diff < 0.01) {
            setBalanceStatus({
                message: "Debet och Kredit balanserar",
                variant: "success"
            });
        } else if (totalDebit > totalCredit) {
            setBalanceStatus({
                message: `Debet är mer än kredit med ${formatCurrency(totalDebit - totalCredit)}`,
                variant: "danger"
            });
        } else if (totalCredit > totalDebit) {
            setBalanceStatus({
                message: `Kredit är mer än debet med ${formatCurrency(totalCredit - totalDebit)}`,
                variant: "danger"
            });
        }
    };

    const fetchVerificationData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/verifications/${verificationId}`),
                { withCredentials: true }
            );

            if (response.data) {
                const vData = response.data;
                setFormData({
                    date: new Date(vData.date),
                    description: vData.description,
                    attachments: vData.attachments || [],
                    transactions: vData.transactions.map(tx => ({
                        ...tx,
                        debit: tx.transaction_type === 'debit' ? parseFloat(tx.amount) : "",
                        credit: tx.transaction_type === 'credit' ? parseFloat(tx.amount) : ""
                    }))
                });

                // Generate annulment transactions
                const reversedTransactions = vData.transactions.map((transaction) => ({
                    ...transaction,
                    transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
                }));
                setAnnulmentTransactions(reversedTransactions);
            }
        } catch (error) {
            console.error("Error fetching verification data:", error);
            setError("Det gick inte att hämta verifikationsdata. Försök igen senare.");
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts?scope=company`), { withCredentials: true });
            const options = result.data.accounts.map((account) => ({
                value: account.account_id,
                label: `${account.account_number} - ${account.account_name}`,
                account_number: account.account_number,
                account_name: account.account_name,
            }));
            setAccounts(options);
        } catch (error) {
            console.error("Error fetching accounts:", error);
            setError("Det gick inte att hämta kontolistan. Försök igen senare.");
        } finally {
            setLoading(false);
        }
    };

    const generateAnnulmentTransactions = () => {
        if (verificationData && verificationData.transactions) {
            const reversedTransactions = verificationData.transactions.map((transaction) => ({
                ...transaction,
                transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
            }));
            setAnnulmentTransactions(reversedTransactions);
        }
    };

    const handleRowChange = (index, field, value) => {
        const newTransactions = [...formData.transactions];

        if (field === "account_id") {
            const selectedAccount = accounts.find((option) => option.value === value);
            if (selectedAccount) {
                newTransactions[index] = {
                    ...newTransactions[index],
                    account_id: selectedAccount.value,
                    account_number: selectedAccount.account_number,
                    account_name: selectedAccount.account_name,
                };
            }
        } else if (field === "debit") {
            // If debit is entered, clear credit
            newTransactions[index] = {
                ...newTransactions[index],
                debit: value,
                credit: value ? "" : newTransactions[index].credit
            };
        } else if (field === "credit") {
            // If credit is entered, clear debit
            newTransactions[index] = {
                ...newTransactions[index],
                credit: value,
                debit: value ? "" : newTransactions[index].debit
            };
        } else {
            newTransactions[index][field] = value;
        }

        setFormData((prev) => ({ ...prev, transactions: newTransactions }));
    };

    const handleAddRow = () => {
        setFormData((prev) => ({
            ...prev,
            transactions: [...prev.transactions, { account_id: "", account_number: "", account_name: "", debit: "", credit: "" }],
        }));
    };

    const handleRemoveRow = (index) => {
        if (formData.transactions.length > 1) {
            const newTransactions = formData.transactions.filter((_, rowIndex) => rowIndex !== index);
            setFormData((prev) => ({ ...prev, transactions: newTransactions }));
        }
    };

    const handleSave = async () => {
        if (!isFormValid()) {
            toast.error("Kontrollera att alla fält är ifyllda och att debet och kredit balanserar");
            return;
        }

        setSavingVerification(true);
        setError(null);

        try {
            // Prepare both verifications in one request
            const verificationRequests = {
                annulment: {
                    date: verificationData.date.split("T")[0],
                    description: `Annulering av verifikat ${verificationData.series}${verificationData.number}`,
                    series: "V",
                    transactions: annulmentTransactions,
                    attachments: formData.attachments
                },
                replacement: {
                    date: formData.date.toISOString().split("T")[0],
                    description: formData.description,
                    series: "V",
                    transactions: formData.transactions.map(tx => ({
                        ...tx,
                        amount: tx.debit || tx.credit,
                        transaction_type: tx.debit ? 'debit' : 'credit'
                    })),
                    attachments: formData.attachments
                }
            };

            // Updated URL to include verification ID
            const response = await axios.post(
                getApiUrl(`${companyId}/verifications/${verificationId}/replace`),
                verificationRequests,
                { withCredentials: true }
            );

            if (!response.data.success) {
                throw new Error(response.data.message || "Failed to update verification");
            }

            toast.success("Verifikatet har uppdaterats");

            // Navigate back with a refresh flag
            const basePath = from || `/${companyId}/verifications`;
            const returnPath = basePath.includes('?')
                ? basePath
                : `${basePath}?updated=true`;

            history.push(returnPath);
        } catch (error) {
            console.error("Error saving verification:", error);
            const errorMessage = error.response?.data?.message || "Det gick inte att uppdatera verifikatet. Försök igen senare.";
            setError(errorMessage);
            toast.error(errorMessage);
            setSavingVerification(false);
        }
    };

    const handleAbort = async () => {
        // Show confirmation if user has made changes
        const hasChanges =
            formData.description !== verificationData.description ||
            formData.date.getTime() !== new Date(verificationData.date).getTime() ||
            formData.transactions.length !== verificationData.transactions.length;

        if (hasChanges) {
            swal({
                title: "Är du säker?",
                text: "Ändringar du gjort kommer att gå förlorade",
                icon: "warning",
                buttons: ["Avbryt", "Ja, avbryt"],
                dangerMode: true,
            }).then((willDiscard) => {
                if (willDiscard) {
                    navigateBack();
                }
            });
        } else {
            navigateBack();
        }
    };

    const navigateBack = () => {
        const basePath = from || `/${companyId}/verifications`;
        history.push(basePath);
    };

    // Tooltip components
    const renderTooltip = (props, what) => {
        switch (what) {
            case "annulment":
                return (
                    <Tooltip {...props}>
                        Detta är verifikatet som kommer att skapas för att nollställa det verifikat du vill ändra.
                    </Tooltip>
                );

            case "original":
                return (
                    <Tooltip {...props}>
                        Detta är det ursprungliga verifikatet som skapades {verificationData?.date.split("T")[0]}.
                    </Tooltip>
                );

            case "change":
                return (
                    <Tooltip {...props}>
                        Detta är det nya modifierade verifikatet som kommer att skapas.
                    </Tooltip>
                );

            default:
                return null;
        }
    };

    // Calculate totals for display
    // Convert amounts to numbers to ensure proper addition (not string concatenation)
    const originalDebitTotal = verificationData?.transactions?.reduce((sum, t) => {
        const amount = t.transaction_type === 'debit' ? Number(t.amount) || 0 : 0;
        return sum + amount;
    }, 0) || 0;

    const originalCreditTotal = verificationData?.transactions?.reduce((sum, t) => {
        const amount = t.transaction_type === 'credit' ? Number(t.amount) || 0 : 0;
        return sum + amount;
    }, 0) || 0;

    // Annulment totals (reversed from original)
    const annulmentDebitTotal = annulmentTransactions.reduce((sum, t) => {
        const amount = t.transaction_type === 'debit' ? Number(t.amount) || 0 : 0;
        return sum + amount;
    }, 0);

    const annulmentCreditTotal = annulmentTransactions.reduce((sum, t) => {
        const amount = t.transaction_type === 'credit' ? Number(t.amount) || 0 : 0;
        return sum + amount;
    }, 0);

    const newDebitTotal = formData.transactions.reduce((sum, t) =>
        sum + parseFloat(t.debit || 0), 0);
    const newCreditTotal = formData.transactions.reduce((sum, t) =>
        sum + parseFloat(t.credit || 0), 0);

    const isBalanced = Math.abs(newDebitTotal - newCreditTotal) <= 0.01;

    if (loading && !verificationData) {
        return (

            <div className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </Spinner>
                <p className="mt-2">Laddar verifikationsdata...</p>
            </div>

        );
    }

    if (!verificationData && !loading && error) {
        return (

            <div>
                <Alert variant="danger">
                    {error || "Ingen verifikationsinformation hittades."}
                </Alert>
                <Button variant="primary" onClick={navigateBack}>
                    Tillbaka till verifikationer
                </Button>
            </div>

        );
    }

    return (
        <>
            <style>{`
                .edit-section {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e2e8f0;
                }
                
                .section-icon {
                    width: 24px;
                    height: 24px;
                    color: #64748b;
                }

                /* Hide number input spinners - Chrome, Safari, Edge, Opera */
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                /* Hide number input spinners - Firefox */
                input[type="number"] {
                    -moz-appearance: textfield;
                }

                .page-header {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1.5rem 2rem;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .page-header-title {
                    color: #1e293b;
                    font-weight: 600;
                }

                .page-header-subtitle {
                    color: #64748b;
                    font-size: 0.875rem;
                }

                .page-header-back {
                    color: #64748b;
                    transition: color 0.2s ease;
                }

                .page-header-back:hover {
                    color: #1e293b;
                }

                .workflow-indicator {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 1.5rem 0;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                }

                .workflow-step {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    text-align: center;
                    min-width: 160px;
                    transition: all 0.2s ease;
                }

                .workflow-step.original {
                    border-color: #e2e8f0;
                    background: #f8fafc;
                }

                .workflow-step.annulment {
                    border-color: #e2e8f0;
                    background: #f8fafc;
                }

                .workflow-step.new {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                }
                
                .workflow-arrow {
                    color: #64748b;
                    font-size: 1.5rem;
                }
                
                .verification-table {
                    background: white;
                    border-radius: 8px;
                    overflow-x: auto;
                    border: 1px solid #e2e8f0;
                }
                
                .table-header {
                    background: #f1f5f9;
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .amount-cell {
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    font-size: 0.875rem;
                }
                
                /* Remove forced scrollbars - let tables adapt to screen size */
                .table-responsive {
                    overflow-x: visible;
                }
                
                /* Main editing table - flexible layout */
                .main-transactions-table {
                    width: 100%;
                    table-layout: fixed; /* Enable fixed layout for proper column control */
                }
                
                .main-transactions-table th:first-child,
                .main-transactions-table td:first-child {
                    width: 45%; /* Account column takes most space */
                }
                
                .main-transactions-table th:nth-child(2),
                .main-transactions-table td:nth-child(2),
                .main-transactions-table th:nth-child(3),
                .main-transactions-table td:nth-child(3) {
                    width: 22.5%; /* Amount columns split remaining space */
                }
                
                .main-transactions-table th:last-child,
                .main-transactions-table td:last-child {
                    width: 10%; /* Trash button column - small but sufficient */
                    text-align: center;
                }
                
                /* Preview tables - auto layout to fit content */
                .preview-table {
                    width: 100%;
                    table-layout: auto;
                }

                .preview-table th:first-child,
                .preview-table td:first-child {
                    white-space: nowrap;
                }

                .preview-table th:nth-child(2),
                .preview-table td:nth-child(2),
                .preview-table th:nth-child(3),
                .preview-table td:nth-child(3) {
                    white-space: nowrap;
                    text-align: right;
                }

                .preview-table tfoot {
                    background: #f1f5f9;
                    border-top: 1px solid #e2e8f0;
                }

                .preview-table tfoot td {
                    font-weight: 600;
                }
                
                /* Ensure Select component is responsive */
                .account-select {
                    width: 100% !important;
                    max-width: 100% !important;
                }
                
                .account-select .css-13cymwt-control {
                    width: 100% !important;
                    min-width: 0 !important;
                }
                
                /* Mobile responsiveness - stack differently on very small screens */
                @media (max-width: 576px) {
                    .container-fluid {
                        padding: 0.5rem;
                    }
                    
                    .edit-section {
                        padding: 1rem;
                    }
                    
                    /* On mobile, account column can be smaller */
                    .main-transactions-table th:first-child,
                    .main-transactions-table td:first-child {
                        width: 40%;
                    }
                    
                    .main-transactions-table th:nth-child(2),
                    .main-transactions-table td:nth-child(2),
                    .main-transactions-table th:nth-child(3),
                    .main-transactions-table td:nth-child(3) {
                        width: 25%;
                    }
                    
                    .main-transactions-table th:last-child,
                    .main-transactions-table td:last-child {
                        width: 10%;
                    }
                }
                
                /* Ensure buttons are always visible and properly sized */
                .trash-button {
                    display: flex !important;
                    align-items: center;
                    justify-content: center;
                    min-width: 32px;
                    min-height: 32px;
                }
                
                .balance-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    margin-bottom: 1rem;
                }
                
                .balance-good {
                    background: #dcfce7;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                }
                
                .balance-bad {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                
                .action-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #374151;
                    text-decoration: none;
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                    font-weight: 500;
                    cursor: pointer;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                
                .action-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .action-button.primary {
                    background: var(--primary);
                    color: white;
                    border-color: var(--primary);
                }
                
                .action-button.primary:hover:not(:disabled) {
                    background: var(--primary-hover);
                    border-color: var(--primary-hover);
                    color: white;
                }
                
                .action-button.secondary {
                    background: #6b7280;
                    color: white;
                    border-color: #6b7280;
                }
                
                .action-button.secondary:hover:not(:disabled) {
                    background: #4b5563;
                    border-color: #4b5563;
                    color: white;
                }
                
                .transaction-row {
                    transition: all 0.2s ease;
                }
                
                .transaction-row:hover {
                    background: #f8fafc;
                }
                
                .add-row-button {
                    color: var(--primary);
                    border: 2px dashed #cbd5e1;
                    background: transparent;
                    padding: 1rem;
                    text-align: center;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                
                .add-row-button:hover {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                }
            `}</style>

            {/* Page Header */}
            <div className="page-header">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <button
                            className="btn btn-link page-header-back p-0 me-3"
                            onClick={navigateBack}
                            disabled={savingVerification}
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <h4 className="page-header-title mb-1">Redigera Verifikat {verificationData?.series}{verificationData?.number}</h4>
                            <div className="page-header-subtitle">{verificationData?.description}</div>
                        </div>
                    </div>
                    <Edit3 size={24} className="text-muted" />
                </div>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {/* Workflow Indicator */}
            <div className="workflow-indicator">
                <div className="workflow-step original">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                        <FileX size={18} className="text-muted" />
                    </div>
                    <div className="fw-medium text-secondary small">Ursprungligt</div>
                    <div className="small text-muted">{verificationData?.series}{verificationData?.number}</div>
                </div>

                <ArrowRight className="workflow-arrow text-muted" size={20} />

                <div className="workflow-step annulment">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                        <X size={18} className="text-muted" />
                    </div>
                    <div className="fw-medium text-secondary small">Annullering</div>
                    <div className="small text-muted">Skapas automatiskt</div>
                </div>

                <ArrowRight className="workflow-arrow text-muted" size={20} />

                <div className="workflow-step new">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                        <Edit3 size={18} style={{ color: 'var(--bs-primary)' }} />
                    </div>
                    <div className="fw-medium small" style={{ color: 'var(--bs-primary)' }}>Nytt Verifikat</div>
                    <div className="small text-muted">Din redigering</div>
                </div>
            </div>

            <Row>
                {/* Left Column: Edit Form */}
                <Col lg={8}>
                    <div className="edit-section">
                        <div className="d-flex align-items-center mb-4">
                            <Edit3 className="section-icon me-2" />
                            <h5 className="mb-0 fw-medium">Redigera Verifikat</h5>
                        </div>

                        {/* Basic Information */}
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        <Calendar size={16} className="me-2" />
                                        Datum
                                    </Form.Label>
                                    <DatePicker
                                        selected={formData.date}
                                        onChange={(date) => setFormData((prev) => ({ ...prev, date }))}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        <FileText size={16} className="me-2" />
                                        Beskrivning
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                        placeholder="Ange beskrivning"
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Balance Status 
                        <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
                            {isBalanced ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                            {balanceStatus.message}
                        </div>
                        */}

                        {/* Transactions Table */}
                        <div className="d-flex align-items-center mb-3">
                            <Calculator className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Transaktioner</h6>
                        </div>

                        <div className="verification-table">
                            <table className="table mb-0 main-transactions-table">
                                <thead className="table-header">
                                    <tr>
                                        <th className="border-0 py-3 px-4 fw-medium text-muted">Konto</th>
                                        <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Debet (SEK)</th>
                                        <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Kredit (SEK)</th>
                                        <th className="border-0 py-3 px-4 fw-medium text-muted"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.transactions.map((row, index) => (
                                        <tr key={index} className="transaction-row">
                                            <td className="py-3 px-4 border-0">
                                                <Select
                                                    options={accounts}
                                                    value={accounts.find((option) => option.value === row.account_id)}
                                                    onChange={(selectedOption) =>
                                                        handleRowChange(index, "account_id", selectedOption ? selectedOption.value : "")
                                                    }
                                                    isDisabled={savingVerification}
                                                    placeholder="Välj konto..."
                                                    className="account-select"
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            width: '100%',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '6px'
                                                        }),
                                                        container: (base) => ({
                                                            ...base,
                                                            width: '100%'
                                                        })
                                                    }}
                                                />
                                            </td>
                                            <td className="py-3 px-4 border-0">
                                                <Form.Control
                                                    type="number"
                                                    value={row.debit}
                                                    onChange={(e) => handleRowChange(index, "debit", e.target.value)}
                                                    placeholder="0.00"
                                                    step="1"
                                                    min="0"
                                                    disabled={savingVerification}
                                                    className="amount-cell text-end"
                                                />
                                            </td>
                                            <td className="py-3 px-4 border-0">
                                                <Form.Control
                                                    type="number"
                                                    value={row.credit}
                                                    onChange={(e) => handleRowChange(index, "credit", e.target.value)}
                                                    placeholder="0.00"
                                                    step="1"
                                                    min="0"
                                                    disabled={savingVerification}
                                                    className="amount-cell text-end"
                                                />
                                            </td>
                                            <td className="py-3 px-4 border-0">
                                                {formData.transactions.length > 1 && (
                                                    <button
                                                        className="btn btn-link text-danger p-1 trash-button"
                                                        onClick={() => handleRemoveRow(index)}
                                                        disabled={savingVerification}
                                                        title="Ta bort rad"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-top bg-light">
                                    <tr>
                                        <td className="py-3 px-4 fw-bold">Summa</td>
                                        <td className="py-3 px-4 text-end fw-bold amount-cell" style={{ color: '#166534' }}>
                                            {formatCurrency(newDebitTotal)}
                                        </td>
                                        <td className="py-3 px-4 text-end fw-bold amount-cell" style={{ color: '#1e40af' }}>
                                            {formatCurrency(newCreditTotal)}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Add Row Button */}
                        <div
                            className="add-row-button mt-3"
                            onClick={handleAddRow}
                            style={{ cursor: savingVerification ? 'not-allowed' : 'pointer' }}
                        >
                            <Plus size={16} className="me-2" />
                            Lägg till transaktion
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-3 mt-4 pt-3 border-top">
                            <button
                                className={`action-button primary ${!isFormValid() || savingVerification ? 'disabled' : ''}`}
                                onClick={handleSave}
                                disabled={!isFormValid() || savingVerification}
                                title={!areTransactionsBalanced() ? "Transaktioner måste balansera för att spara" : ""}
                            >
                                {savingVerification ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        Sparar ändringar...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Spara Ändringar
                                    </>
                                )}
                            </button>

                            <button
                                className="action-button secondary"
                                onClick={handleAbort}
                                disabled={savingVerification}
                            >
                                <X size={16} />
                                Avbryt
                            </button>
                        </div>
                    </div>
                </Col>

                {/* Right Column: Reference Information */}
                <Col lg={4}>
                    {/* Original Verification */}
                    <div className="edit-section">
                        <div className="d-flex align-items-center mb-3">
                            <FileX className="section-icon me-2" style={{ color: '#dc2626' }} />
                            <h6 className="mb-0 fw-medium">Ursprungligt Verifikat</h6>
                            <span className="badge bg-danger ms-2">Raderas</span>
                        </div>

                        <div className="small mb-3">
                            <div><strong>Nummer:</strong> {verificationData?.series}{verificationData?.number}</div>
                            <div><strong>Datum:</strong> {verificationData?.date?.split("T")[0]}</div>
                            <div><strong>Beskrivning:</strong> {verificationData?.description}</div>
                        </div>

                        <div className="verification-table">
                            <table className="table table-sm mb-0 preview-table">
                                <thead className="table-header">
                                    <tr>
                                        <th className="border-0 py-2 px-3 small">Konto</th>
                                        <th className="border-0 py-2 px-3 small text-end">Debet</th>
                                        <th className="border-0 py-2 px-3 small text-end">Kredit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {verificationData?.transactions?.map((transaction, index) => (
                                        <tr key={index}>
                                            <td className="py-2 px-3 border-0 small">
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            {transaction.account_name}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>{transaction.account_number}</span>
                                                </OverlayTrigger>
                                            </td>
                                            <td className="py-2 px-3 border-0 text-end small amount-cell">
                                                {transaction.transaction_type === 'debit' ?
                                                    formatCurrency(transaction.amount) : '—'}
                                            </td>
                                            <td className="py-2 px-3 border-0 text-end small amount-cell">
                                                {transaction.transaction_type === 'credit' ?
                                                    formatCurrency(transaction.amount) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="py-2 px-3 small">Summa</td>
                                        <td className="py-2 px-3 text-end small amount-cell">
                                            {formatCurrency(originalDebitTotal)}
                                        </td>
                                        <td className="py-2 px-3 text-end small amount-cell">
                                            {formatCurrency(originalCreditTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Annulment Verification Preview */}
                    <div className="edit-section">
                        <div className="d-flex align-items-center mb-3">
                            <X className="section-icon me-2" style={{ color: '#f59e0b' }} />
                            <h6 className="mb-0 fw-medium">Annulleringsverifikat</h6>
                            <span className="badge bg-warning ms-2">Skapas</span>
                        </div>

                        <div className="small mb-3">
                            <div><strong>Beskrivning:</strong> Annulering av verifikat {verificationData?.series}{verificationData?.number}</div>
                            <div><strong>Datum:</strong> {verificationData?.date?.split("T")[0]}</div>
                        </div>

                        <div className="verification-table">
                            <table className="table table-sm mb-0 preview-table">
                                <thead className="table-header">
                                    <tr>
                                        <th className="border-0 py-2 px-3 small">Konto</th>
                                        <th className="border-0 py-2 px-3 small text-end">Debet</th>
                                        <th className="border-0 py-2 px-3 small text-end">Kredit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {annulmentTransactions.map((transaction, index) => (
                                        <tr key={index}>
                                            <td className="py-2 px-3 border-0 small">
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            {transaction.account_name}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>{transaction.account_number}</span>
                                                </OverlayTrigger>
                                            </td>
                                            <td className="py-2 px-3 border-0 text-end small amount-cell">
                                                {transaction.transaction_type === 'debit' ?
                                                    formatCurrency(transaction.amount) : '—'}
                                            </td>
                                            <td className="py-2 px-3 border-0 text-end small amount-cell">
                                                {transaction.transaction_type === 'credit' ?
                                                    formatCurrency(transaction.amount) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="py-2 px-3 small">Summa</td>
                                        <td className="py-2 px-3 text-end small amount-cell">
                                            {formatCurrency(annulmentDebitTotal)}
                                        </td>
                                        <td className="py-2 px-3 text-end small amount-cell">
                                            {formatCurrency(annulmentCreditTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Information Panel */}
                    {/*<div className="edit-section">
                        <div className="d-flex align-items-center mb-3">
                            <Info className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Information</h6>
                        </div>

                        <div className="small text-muted">
                            <p>När du sparar ändringarna kommer följande att hända:</p>
                            <ol className="ps-3">
                                <li>Det ursprungliga verifikatet annulleras</li>
                                <li>Ett annulleringsverifikat skapas</li>
                                <li>Ditt nya verifikat skapas med dina ändringar</li>
                            </ol>
                            <p className="mb-0">Denna process säkerställer korrekt revisionsspår.</p>
                        </div>
                    </div>*/}
                </Col>
            </Row>
        </>
    );
};

export default EditVerification;
